<template>
  <!-- FORM -->
  <form data-vv-scope="file-form" @submit.prevent="save" autocomplete="off">
    <div>
      <b-alert v-model="showErrorMessage" class="mt-2 mb-3 p-3" variant="danger" dismissible>
        <span class="text-xs primary-text">
          Existen errores que debes corregir antes de continuar!
        </span>
      </b-alert>
      <div>
        <!-- NAME -->
        <b-form-group
          label="Nombre para documento"
          label-for="name"
        >
          <b-form-input
            id="name"
            name="name"
            data-cy="document-name"
            class="w-100"
            autocomplete="off"
            data-vv-as="Nombre para documento"
            v-model="formValues.name"
            v-validate="'required|max:255'"
            :state="!errors.has('file-form.name') && formValues.name.length > 0"
          />
          <FormError
            attributeName="file-form.name"
            :errorsForm="errors"
          />
        </b-form-group>
        <!-- IS PUBLIC? -->
        <b-form-group
          label="¿Es un documento de acceso público?"
          label-for="isPublic"
        >
          <vs-switch
            id="isPublic"
            name="isPublic"
            data-cy="public-document"
            color="primary"
            vs-icon-on="done"
            vs-icon-off="block"
            v-model="formValues.isPublic"
            class="w-16"
          >
            <span class="pl-1" slot="on">SI</span>
            <span slot="off">NO</span>
          </vs-switch>
        </b-form-group>
        <!-- PUBLICATION STATE, IS PUBLISHED -->
        <b-form-group
          label="Estado de publicación:"
          label-for="publicationState"
        >
          <b-form-select
            class="w-100"
            label=""
            id="publicationState"
            name="publicationState"
            data-cy="publication-status"
            v-model="formValues.publicationState"
            :options="publicationStateOptions"
          />
        </b-form-group>
        <!-- EXTERNAL VERSION -->
        <b-form-group
          label="Versión externa"
          label-for="externalVersion"
        >
          <b-form-input
            class="w-100"
            autocomplete="off"
            data-vv-as="Versión externa"
            id="externalVersion"
            maxlength="10"
            name="externalVersion"
            data-cy="external-version"
            v-model.trim="formValues.externalVersion"
          />
          <FormError
            attributeName="file-form.externalVersion"
            :errorsForm="errors"
          />
        </b-form-group>
        <!-- EXPIRATION TYPE -->
        <b-form-group
          label="Tipo de expiración"
          label-for="expirationType"
        >
          <b-form-select
            class="w-100"
            id="expirationType"
            name="expirationType"
            data-cy="expiration-type"
            v-model="selectedExpirationType"
            v-validate="'required'"
            data-vv-as="Tipo de expiración"
            :options="expirationTypeOptions"
          />
          <FormError
            attributeName="file-form.expirationType"
            :errorsForm="errors"
          />
        </b-form-group>
        <!-- EXPIRATION IN X DAYS -->
        <b-form-group
          v-if="selectedExpirationType === expirationTypes.UNIQUE_EXPIRATION_IN_X_DAYS"
          label="Días"
          label-for="expirationInXDays"
        >
          <b-form-input
            class=""
            data-vv-as="Días"
            id="expirationInXDays"
            name="expirationInXDays"
            data-cy="day"
            v-model.trim="expirationInXDays"
            v-validate="'required|numeric|min_value:1|max_value:9999'"
            :state="!errors.has('file-form.expirationInXDays') &&
            expirationInXDays > 0"
            type="number" min="1" max="9999"
          />
          <FormError
            attributeName="file-form.expirationInXDays"
            :errorsForm="errors"
          />
        </b-form-group>
        <!-- EXPIRATION DATE -->
        <b-form-group
          v-if="selectedExpirationType !== expirationTypes.NO_EXPIRATION"
          :label="expirationLabel"
          label-for="expirationDate"
        >
          <!-- Use cursor keys to navigate calendar dates -->
          <b-form-datepicker
            id="expirationDate"
            name="expirationDate"
            data-cy="calendar"
            data-vv-as="Fecha de expiración"
            placeholder="Selecciona una fecha"
            v-model="formValues.expirationDate"
            class="form-control bg-white"
            v-validate="'required'"
            label-help="Puede usar las flechas del teclado y espacio"
            locale="es"
            :min="minDate"
            :state="!errors.has('file-form.expirationDate') &&
              formValues.expirationDate !== null"
            :disabled="selectedExpirationType
              === expirationTypes.UNIQUE_EXPIRATION_IN_X_DAYS"
          />
          <FormError
            attributeName="file-form.expirationDate"
            :errorsForm="errors"
          />
        </b-form-group>
        <!-- EXPIRATION HOUR -->
        <b-form-group
          v-if="selectedExpirationType !== expirationTypes.NO_EXPIRATION"
          label="Hora de expiración"
          label-for="expirationHour"
        >
          <b-form-select
            id="expirationHour"
            name="expirationHour"
            data-cy="hour"
            :options="hours"
            data-vv-as="Hora de expiración"
            v-on:change="setExpirationNotification"
            v-model="formValues.expirationHour"
            class="w-100"
            v-validate="'required'"
            :state="!errors.has('file-form.expirationHour') &&
              formValues.expirationHour !== null"
          >
          </b-form-select>
          <FormError
            attributeName="file-form.expirationHour"
            :errorsForm="errors"
          />
        </b-form-group>
        <!-- NOTIFICATION DATE -->
        <b-form-group
          v-if="formValues.selectedExpirationType !== expirationTypes.NO_EXPIRATION
            && formValues.expirationDate && formValues.expirationHour"
          label="Fecha de notificación"
          label-for="notificationDate"
        >
          <b-form-datepicker
            id="notificationDate"
            name="notificationDate"
            data-cy="calendar"
            placeholder="Selecciona una fecha"
            :value="formValues.notificationDate"
            class="form-control bg-white"
            locale="es"
            :min="getSelectedElement.creationDate.substring(0,10)"
            :max="formValues.expirationDate"
            label-help="Puede usar las flechas del teclado y espacio"
            @input="(val) => setNotificationDate(val)"
          />
          <FormError
            attributeName="file-form.notificationDate"
            :errorsForm="errors"
          />
        </b-form-group>
        <!-- NOTIFICATION HOUR -->
        <b-form-group
          v-if="formValues.selectedExpirationType !== expirationTypes.NO_EXPIRATION
            && formValues.expirationDate && formValues.expirationHour"
          label="Hora de notificación"
          label-for="notificationHour"
        >
          <b-form-select
            id="notificationHour"
            name="notificationHour"
            data-cy="hour"
            :options="hours"
            data-vv-as="hora de notificación"
            v-model="formValues.notificationHour"
            class="w-100"
            v-validate="'between'"
            :state="!errors.has('file-form.notificationHour')"
          />
          <!-- TODO: Cambiar mensaje de error al por defecto.
          Por ahora no funciona, posible bug -->
          <b-form-invalid-feedback
            class="d-none w-100"
            :state="!errors.has('file-form.notificationHour')">
            La fecha y hora debe ser antes de la expiración y posterior a la de creación
          </b-form-invalid-feedback>
          <FormError
            attributeName="notificationHour"
            :errorsForm="errors"
          />
        </b-form-group>
      </div>
      <div class="m-top-20 w-100 d-flex justify-end">
        <b-button
          class="mt-5 ml-3"
          v-on:click="save"
          variant="outline-primary"
          data-cy="save"
        >
          Guardar
          <span v-show="loading" class="spinner-border spinner-border-sm text-white"/>
        </b-button>
      </div>
    </div>
  </form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Vue from 'vue';

import FormError from '@/febos/global/documentManagement/components/FormError';
import ApprovalStateTypes from '@/febos/global/documentManagement/mixins/ApprovalStateTypes';
import PublicationStateTypes from '@/febos/global/documentManagement/mixins/PublicationStateTypes';
import ValidityStateTypes from '@/febos/global/documentManagement/mixins/ValidityStateTypes';
import ExpirationTypes from '@/febos/global/documentManagement/mixins/ExpirationTypes';
import hours from '@/febos/global/documentManagement/mixins/HoursOptions';
import { SAVAL } from '@/constants/companies';

export default {
  name: 'EditFileForm',
  props: {
    isEdit: {
      type: Boolean,
      required: false,
      default: () => false
    },
    title: {
      type: String,
      required: false,
      default: () => 'Editar documento'
    }
  },
  data() {
    return {
      minDate: this.$moment().format('YYYY-MM-DD'),
      selectedFile: false,
      loading: false,
      showErrorMessage: false,
      hours,
      formValues: {
        id: '',
        name: '',
        extension: '',
        path: '',
        externalVersion: '',
        isPublic: false,
        publicationState: PublicationStateTypes.DRAFT,
        expirationDate: null,
        expirationHour: null,
        notificationDate: null,
        notificationHour: null,
      },
      selectedExpirationType: ExpirationTypes.NO_EXPIRATION,
      expirationInXDays: '',
      fileToUpload: {},
      selectedTab: 0,
      tabs: [],
      approvalStateTypes: ApprovalStateTypes,
      publicationStateTypes: PublicationStateTypes,
      validityStateTypes: ValidityStateTypes,
      expirationTypes: ExpirationTypes,
      regexValidation: /.*/,
      expirationTypeOptions: [
        {
          value: ExpirationTypes.NO_EXPIRATION,
          text: 'Sin expiración'
        },
        {
          value: ExpirationTypes.ANNUAL_CYCLICAL_EXPIRATION,
          text: 'Expiración cíclica anual'
        },
        {
          value: ExpirationTypes.MONTHLY_CYCLICAL_EXPIRATION,
          text: 'Expiración cíclica mensual'
        },
        {
          value: ExpirationTypes.UNIQUE_EXPIRATION_DATE,
          text: 'Expiración única con fecha exacta'
        },
        {
          value: ExpirationTypes.UNIQUE_EXPIRATION_IN_X_DAYS,
          text: 'Expiración única en (x) días'
        }
      ],
      publicationStateOptions: [
        {
          value: PublicationStateTypes.PUBLISHED,
          text: 'Publicado'
        },
        {
          value: PublicationStateTypes.DRAFT,
          text: 'Borrador'
        }
      ]
    };
  },
  components: {
    FormError
  },
  created() {
    this.validateNotificationTime();
  },
  mounted() {
    this.formValues.id = this.getSelectedElement.id;
    this.formValues.name = this.getSelectedElement.name.split('.').shift();
    this.formValues.extension = this.getSelectedElement.name.split('.').pop().toLowerCase();
    this.formValues.externalVersion = this.getSelectedElement.externalVersion || '';
    this.formValues.isPublic = this.getSelectedElement.isPublic || false;
    this.formValues.publicationState = this.getSelectedElement.publicationState;
    this.selectedExpirationType = this.getSelectedElement.expirationType;
    this.formValues.expirationHour = this.getSelectedElement.expirationDate?.substring(11, 13)
      || null;
    this.formValues.notificationDate = this.getSelectedElement.notificationDate?.substring(0, 10);
    this.formValues.notificationHour = this.getSelectedElement.notificationDate?.substring(11, 13)
      || null;
    const currentDate = this.$moment().format('YYYY-MM-DD');
    const expirationDate = this.$moment(this.getSelectedElement.expirationDate).format('YYYY-MM-DD');
    if (this.selectedExpirationType === ExpirationTypes.UNIQUE_EXPIRATION_IN_X_DAYS) {
      const xDays = this.$moment(expirationDate).diff(this.$moment(currentDate), 'days');
      this.expirationInXDays = xDays;
    }
    if (this.selectedExpirationType !== ExpirationTypes.NO_EXPIRATION) {
      this.formValues.expirationDate = expirationDate;
    }
  },
  methods: {
    ...mapActions('DocManagement', [
      'uploadFile',
      'updateFile'
    ]),
    ...mapActions('Modals', [
      'closeModal'
    ]),
    ...mapActions('Modals', [
      'closeModal'
    ]),
    setNotificationDate(val) {
      this.formValues.notificationDate = val;
    },
    async save() {
      if (this.loading) {
        return;
      }
      const formCheck = await this.$validator.validateAll('file-form');
      if (formCheck) {
        this.showErrorMessage = false;
        this.loading = true;
        await this.updateFile({
          repositoryId: this.repositoryId,
          folderId: this.getSelectedFolderId,
          file: {
            archivoId: this.formValues.id,
            carpetaId: this.getSelectedFolderId,
            nombre: `${this.formValues.name}.${this.formValues.extension}`,
            esPublico: this.isPublicLabel,
            idioma: 'es',
            versionExterna: this.formValues.externalVersion,
            estadoVigencia: this.getValidityState(),
            estadoAprobacion: this.approvalStateTypes.NOT_APPLY,
            estadoPublicacion: this.formValues.publicationState,
            tipoExpiracion: this.selectedExpirationType,
            fechaVencimiento: this.getExpirationDate(),
            fechaNotificacion: this.getNotificationDate()
          }
        });
        this.$bvToast.toast(
          'El documento ha sido actualizado correctamente!',
          {
            title: this.title,
            variant: 'primary',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 15000
          }
        );
        this.loading = false;
        this.cleanForm();
      } else {
        this.showErrorMessage = true;
      }
    },
    cleanForm() {
      this.formValues = {
        id: '',
        name: '',
        extension: '',
        path: '',
        externalVersion: '',
        isPublic: false,
        publicationState: PublicationStateTypes.DRAFT,
        expirationDate: null
      };
      this.selectedExpirationType = ExpirationTypes.NO_EXPIRATION;
      this.expirationInXDays = '';
    },
    getValidityState() {
      if (this.selectedExpirationType === ExpirationTypes.NO_EXPIRATION) {
        return ValidityStateTypes.NOT_APPLY;
      }
      return ValidityStateTypes.VALID;
    },
    getExpirationDate() {
      let expirationDate = null;
      if (this.formValues.expirationDate) {
        expirationDate = `${this.formValues.expirationDate} ${this.formValues.expirationHour.substring(0, 2)}:00:00`;
      } else if (this.selectedExpirationType === ExpirationTypes.NO_EXPIRATION) {
        return expirationDate;
      }
      return expirationDate;
    },
    getNotificationDate() {
      let notificationDate = null;
      if (this.formValues.notificationDate && this.formValues.notificationHour) {
        notificationDate = `${this.formValues.notificationDate} ${this.formValues.notificationHour.substring(0, 2)}:00:00`;
      } else if (this.selectedExpirationType === ExpirationTypes.NO_EXPIRATION) {
        return notificationDate;
      }
      return notificationDate;
    },
    validateNotificationTime() {
      // Crea validacion de libreria VeeValidate customizada
      const self = this;
      this.$validator.extend('between', {
        getMessage(field) {
          return `La ${field} debe ser anterior a la de expiración`;
        },
        validate() {
          /* Parseo las fechas para obtener numeros y poder compararlos.
          Agrego la T para que el formato sea válido */
          const startDate = self.getSelectedElement.creationDate.split(' ')[0];
          const startTime = self.getSelectedElement.creationDate.split(' ')[1];
          const startDateTime = Date.parse(`${startDate}T${startTime}`);

          const { expirationDate } = self.formValues;
          const expirationTime = self.formValues.expirationHour;
          const endDateTime = Date.parse(`${expirationDate}T${expirationTime}:00:00`);

          const { notificationDate } = self.formValues;
          const notificationTime = self.formValues.notificationHour;
          const notificationDateTime = Date.parse(`${notificationDate}T${notificationTime}:00:00`);

          return notificationDateTime > startDateTime && notificationDateTime < endDateTime;
        }
      });
    },
    setExpirationNotification() {
      if (this.isSaval
        && (this.formValues.notificationDate === null || !this.formValues.notificationDate)
      ) {
        this.formValues.notificationDate = Vue.moment().add(60, 'days').format('YYYY-MM-DD');
      }
    }
  },
  computed: {
    ...mapGetters('Modals', [
      'modalName'
    ]),
    ...mapGetters('DocManagement', [
      'getSelectedRepositoryId',
      'getSelectedFolderId',
      'getSelectedElement',
      'getTreeElements',
      'getRepositories'
    ]),
    ...mapGetters('Empresas', [
      'company'
    ]),
    isSaval() {
      return this.company.iut === SAVAL.iut;
    },
    repositoryId() {
      let response = '';
      if (this.getSelectedFolderId) {
        response = this.getTreeElements[this.getSelectedFolderId].repositoryId;
      }
      if (this.getSelectedRepositoryId) {
        response = this.getSelectedRepositoryId;
      }
      return response;
    },
    isPublicLabel() {
      if (this.formValues.isPublic) {
        return 'S';
      }
      return 'N';
    },
    expirationLabel() {
      if (this.selectedExpirationType === ExpirationTypes.UNIQUE_EXPIRATION_DATE) {
        return 'Fecha de expiración';
      }
      return 'Primera fecha de expiración';
    },
  },
  watch: {
    expirationInXDays(val) {
      if (this.selectedExpirationType === ExpirationTypes.UNIQUE_EXPIRATION_IN_X_DAYS) {
        this.formValues.expirationDate = this.$moment().add(val, 'd').format('YYYY-MM-DD');
      }
    },
    selectedExpirationType(val) {
      if (val === ExpirationTypes.NO_EXPIRATION) {
        this.$validator.reset();
        this.formValues.expirationDate = null;
        this.formValues.expirationHour = null;
        this.formValues.notificationDate = null;
        this.formValues.notificationHour = null;
      }
    }
  }
};
</script>
<style lang="css" scoped>
.tab-selected {
  border-bottom-width: 2px;
  border-bottom-color: rgb(var(--vs-primary));
  border-bottom-style: solid;
}
.invalid-feedback {
  font-size: 12px;
  color: #AD8ABD;
}
</style>
